import { createId } from "@paralleldrive/cuid2";
import type { ResumeDto } from "@stylingcv/dto";
import type { CustomSectionGroup, ResumeData, SectionKey } from "@stylingcv/schema";
import { defaultResumeData, defaultSection } from "@stylingcv/schema";
import { removeItemInLayout } from "@stylingcv/utils";
import debounce from "lodash.debounce";
import _set from "lodash.set";
import type { TemporalState } from "zundo";
import { temporal } from "zundo";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useStoreWithEqualityFn } from "zustand/traditional";

import { debouncedUpdateResume } from "../services/resume";
import { useAuthStore } from "./auth";

const LOCAL_STORAGE_KEY = "guest-resume";

/** Logged-in users on /builder/try still use placeholder IDs until a real resume is loaded — never PATCH those. */
const shouldPersistResumeToServer = (resumeId: string | undefined): boolean =>
  Boolean(resumeId && resumeId !== "default" && resumeId !== "guest-resume");

// Debounced function to save guest resume
const debouncedSaveGuestResume = debounce(async (resume: ResumeDto) => {
  try {
    const { GuestSessionService } = await import("../services/guest-session");
    await GuestSessionService.saveResumeData(resume);
  } catch (error) {
    console.error("Failed to save guest resume to session:", error);
  }
}, 1000);

// Ensure data integrity for any paths
const ensureDataPath = (resumeData: ResumeData, path: string, value: unknown): ResumeData => {
  try {
    return _set({ ...resumeData }, path, value);
  } catch {
    // If path setting fails, recreate the path with default structures
    const result = { ...resumeData };

    // Handle specific common paths
    if (path.startsWith("sections.") && !result.sections) {
      result.sections = { ...defaultResumeData.sections };
    }

    if (path.startsWith("metadata.") && !result.metadata) {
      result.metadata = { ...defaultResumeData.metadata };
    }

    // Try again after ensuring parent structures exist
    return _set(result, path, value);
  }
};

type ResumeStore = {
  resume: ResumeDto;

  // Actions
  setValue: (path: string, value: unknown) => void;
  addItem: (sectionId: SectionKey, item: unknown) => void;

  // Custom Section Actions
  addSection: () => void;
  removeSection: (sectionId: SectionKey) => void;
};

// Create the store and make it globally accessible for session management
export const useResumeStore = create<ResumeStore>()(
  temporal(
    immer((set) => ({
      resume: {
        id: "default",
        userId: "guest",
        title: "New Resume", // ✅ FIXED: Removed t macro from module level
        slug: "new-resume",
        visibility: "private",
        locked: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        data: defaultResumeData,
      } as ResumeDto,
      setValue: (path, value) => {
        set((state) => {
          if (path === "visibility") {
            state.resume.visibility = value as "public" | "private";
          } else if (path === "title") {
            state.resume.title = value as string;
          } else {
            // Ensure we have a valid data structure before setting values
            if (!state.resume.data) {
              state.resume.data = { ...defaultResumeData };
            }

            try {
              state.resume.data = ensureDataPath(state.resume.data, path, value);
            } catch {
              // If all else fails, reconstruct with default data + the specific change
              state.resume.data = _set({ ...defaultResumeData }, path, value);
            }
          }

          // Check if user is logged in before sending API request
          const isLoggedIn = useAuthStore.getState().user !== null;

          if (isLoggedIn && shouldPersistResumeToServer(state.resume.id)) {
            void debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
          } else {
            // For guest users, save to local storage and guest session
            try {
              localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.resume.data));

              // Also save full resume to guest session if it has a valid ID
              if (
                state.resume.id &&
                state.resume.id !== "default" &&
                state.resume.id !== "guest-resume"
              ) {
                // Use debounced save to prevent too many API calls
                void debouncedSaveGuestResume(JSON.parse(JSON.stringify(state.resume)));
              }
            } catch {
              // Silent fail
            }
          }
        });
      },

      addItem: (sectionId: string, item: any) => {
        set((state) => {
          try {
            // Ensure resume data structure exists
            if (!state.resume.data) {
              state.resume.data = { ...defaultResumeData };
            }

            // Handle custom sections
            if (sectionId.startsWith("custom.")) {
              const customId = sectionId.replace("custom.", "");
              if (!state.resume.data.sections.custom[customId]) {
                state.resume.data.sections.custom[customId] = {
                  ...defaultSection,
                  id: customId,
                  name: customId.charAt(0).toUpperCase() + customId.slice(1),
                  items: [],
                };
              }
              (state.resume.data.sections.custom[customId] as any).items.push(item);
            } else {
              // For regular sections
              const sectionKey = sectionId as keyof typeof state.resume.data.sections;

              // Ensure the section exists
              if (!state.resume.data.sections[sectionKey]) {
                // Create section with default values if it doesn't exist
                state.resume.data.sections[sectionKey] = {
                  ...defaultSection,
                  id: sectionId,
                  name: sectionId.charAt(0).toUpperCase() + sectionId.slice(1),
                  items: [],
                } as any;
              }

              // Handle different section types
              const section = state.resume.data.sections[sectionKey];
              if ("items" in section && Array.isArray(section.items)) {
                // Regular section with items array
                (section.items as any[]).push(item);
              } else if (sectionKey === "summary" && "content" in section) {
                // Summary section - append to content
                section.content = section.content + "\n" + item.content;
              }
            }

            // Save to server or localStorage
            const isLoggedIn = useAuthStore.getState().user !== null;

            if (isLoggedIn && shouldPersistResumeToServer(state.resume.id)) {
              void debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
            } else {
              // For guest users, save to local storage
              try {
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.resume.data));
              } catch {
                // Silent fail
              }
            }
          } catch {
            // Silent fail
          }
        });
      },
      addSection: () => {
        const section: CustomSectionGroup = {
          ...defaultSection,
          id: createId(),
          name: "Custom Section",
          items: [],
        };

        set((state) => {
          // Ensure data structure exists
          if (!state.resume.data) {
            state.resume.data = { ...defaultResumeData };
          }

          // Ensure metadata and layout exist
          if (!state.resume.data.metadata) {
            state.resume.data.metadata = { ...defaultResumeData.metadata };
          }

          if (state.resume.data.metadata.layout.length === 0) {
            state.resume.data.metadata.layout = [...defaultResumeData.metadata.layout];
          }

          // Ensure sections and custom sections exist
          if (!state.resume.data.sections) {
            state.resume.data.sections = { ...defaultResumeData.sections };
          }

          if (!state.resume.data.sections.custom) {
            state.resume.data.sections.custom = {};
          }

          const lastPageIndex = state.resume.data.metadata.layout.length - 1;
          if (!state.resume.data.metadata.layout[lastPageIndex]) {
            state.resume.data.metadata.layout[lastPageIndex] = [[], []];
          }

          if (!state.resume.data.metadata.layout[lastPageIndex][0]) {
            state.resume.data.metadata.layout[lastPageIndex][0] = [];
          }

          state.resume.data.metadata.layout[lastPageIndex][0].push(`custom.${section.id}`);
          state.resume.data.sections.custom[section.id] = section;

          // Check if user is logged in before sending API request
          const isLoggedIn = useAuthStore.getState().user !== null;

          if (isLoggedIn && shouldPersistResumeToServer(state.resume.id)) {
            void debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
          } else {
            // For guest users, save to local storage and guest session
            try {
              localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.resume.data));

              // Also save full resume to guest session if it has a valid ID
              if (
                state.resume.id &&
                state.resume.id !== "default" &&
                state.resume.id !== "guest-resume"
              ) {
                // Use debounced save to prevent too many API calls
                void debouncedSaveGuestResume(JSON.parse(JSON.stringify(state.resume)));
              }
            } catch {
              // Silent fail
            }
          }
        });
      },
      removeSection: (sectionId: SectionKey) => {
        set((state) => {
          // Ensure data structure exists
          if (!state.resume.data.metadata || !state.resume.data.sections) {
            return; // Nothing to remove
          }

          // Remove the section from the layout
          try {
            removeItemInLayout(sectionId, state.resume.data.metadata.layout);
          } catch {
            // Silent fail
          }

          // For custom sections, remove them from the custom sections object
          if (sectionId.startsWith("custom.")) {
            const id = sectionId.split("custom.")[1];
            if (state.resume.data.sections.custom && state.resume.data.sections.custom[id]) {
              // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
              delete state.resume.data.sections.custom[id];
            }
          } else {
            // For core sections, we need to set visibility to false
            const sections = state.resume.data.sections as ResumeData["sections"];
            if (
              sections[sectionId as keyof typeof sections] &&
              typeof sections[sectionId as keyof typeof sections] === "object"
            ) {
              (sections[sectionId as keyof typeof sections] as { visible: boolean }).visible =
                false;
            }
          }

          // Check if user is logged in before sending API request
          const isLoggedIn = useAuthStore.getState().user !== null;

          if (isLoggedIn && shouldPersistResumeToServer(state.resume.id)) {
            void debouncedUpdateResume(JSON.parse(JSON.stringify(state.resume)));
          } else {
            // For guest users, save to local storage and guest session
            try {
              localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.resume.data));

              // Also save full resume to guest session if it has a valid ID
              if (
                state.resume.id &&
                state.resume.id !== "default" &&
                state.resume.id !== "guest-resume"
              ) {
                // Use debounced save to prevent too many API calls
                void debouncedSaveGuestResume(JSON.parse(JSON.stringify(state.resume)));
              }
            } catch {
              // Silent fail
            }
          }
        });
      },
    })),
    {
      limit: 100,
      wrapTemporal: (fn) => devtools(fn),
      partialize: ({ resume }) => ({ resume }),
    },
  ),
);

export const useTemporalResumeStore = <T>(
  selector: (state: TemporalState<Pick<ResumeStore, "resume">>) => T,
  equality?: (a: T, b: T) => boolean,
) => useStoreWithEqualityFn(useResumeStore.temporal, selector, equality);

// Add this function to migrate signature data
export const migrateSignature = (state: ResumeStore) => {
  try {
    // Add proper null checks for data and metadata
    if (!state.resume.data) return;

    // Check if metadata.signature exists and is not empty
    const signatureText = state.resume.data.metadata.signature;

    if (signatureText) {
      // Ensure sections exists
      if (!state.resume.data.sections) {
        state.resume.data.sections = { ...defaultResumeData.sections };
      }

      // Ensure sections and signature section exists
      if (!state.resume.data.sections.signature) {
        state.resume.data.sections.signature = {
          id: "signature",
          name: "Signature",
          visible: true,
          columns: 1,
          separateLinks: true,
          items: [],
        };
      }

      // Add signature item if needed
      if (
        !state.resume.data.sections.signature.items ||
        state.resume.data.sections.signature.items.length === 0
      ) {
        state.resume.data.sections.signature.items = [
          {
            id: createId(),
            visible: true,
            content: signatureText,
          },
        ];
      } else if (state.resume.data.sections.signature.items.length > 0) {
        // Update existing signature item
        state.resume.data.sections.signature.items[0].content = signatureText;
      }

      // Clear the old metadata signature
      state.resume.data.metadata.signature = "";
    } else {
      // If no signature text but the section exists, make sure it has at least one item
      if (
        state.resume.data.sections.signature &&
        (!state.resume.data.sections.signature.items ||
          state.resume.data.sections.signature.items.length === 0)
      ) {
        state.resume.data.sections.signature.items = [
          {
            id: createId(),
            visible: true,
            content: "",
          },
        ];
      }
    }
  } catch {
    // Silent fail
  }
};

// Make the store globally accessible for session management
if (typeof window !== "undefined") {
  (window as any).__resumeStore = useResumeStore;
}
